import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { LogoButton } from '../buttons/LogoButton';
import { ButtonRegular } from '../buttons/ButtonRegular';
import LoadingOverlay from '../LoadingOverlay';
import LogoIcon from '../../../assets/images/Learnit_logo.svg';
import { NAVIGATE_PATH } from '../../../helpers/constants';
import { icons } from '../../../helpers/icons';
import { joinWaitlist } from '../../../requests';
import { showToast } from '../../../helpers';

interface IWaitlistOverlay {
  onSkip?: () => void;
  sfLearnitCourseId?: string;
}

const WaitlistOverlay: React.FC<IWaitlistOverlay> = ({
  onSkip,
  sfLearnitCourseId,
}) => {
  const navigate = useNavigate();

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      navigate(NAVIGATE_PATH.upcomingClasses);
    }
  };

  const joinWaitlistMutation = useMutation({
    mutationFn: (courseId: string) => joinWaitlist(courseId),
    onSuccess: () => {
      showToast(
        'You have been added to the waitlist. We will notify you when a spot becomes available.',
        'success',
        { duration: 12000 },
      );
      navigate(NAVIGATE_PATH.upcomingClasses);
    },
    onError: (error) => {
      console.error(error);
      showToast(
        'Failed to join the waitlist. Please try again later.',
        'error',
        {
          duration: 12000,
        },
      );
      navigate(NAVIGATE_PATH.upcomingClasses);
    },
  });

  const handleJoinWaitlist = () => {
    if (!sfLearnitCourseId) {
      showToast('Invalid course information', 'error');
      navigate(NAVIGATE_PATH.upcomingClasses);
      return;
    }

    joinWaitlistMutation.mutate(sfLearnitCourseId);
  };

  if (joinWaitlistMutation.isPending) {
    return (
      <LoadingOverlay text="Please wait while we add you to the waitlist. This may take a few seconds." />
    );
  }

  return (
    <div className="absolute top-0 left-0 w-screen h-screen flex flex-col bg-white z-[9999]">
      <div className="pt-6 mx-auto auth-container">
        <LogoButton logo={LogoIcon} />
      </div>
      <div className="flex flex-col items-center justify-end px-4 h-1/2 sm:px-6">
        <div className="flex flex-col items-center gap-4 sm:gap-6">
          <div className="flex items-center justify-center w-48 h-48 p-4 bg-white sm:w-56 sm:h-56 md:w-64 md:h-64 sm:p-6 rounded-2xl">
            <img
              src={icons.fullyBooked}
              alt="Fully booked"
              className="w-20 sm:w-24 md:w-28"
            />
          </div>
          <div className="text-base sm:text-lg font-bold leading-relaxed text-center text-grey-700 w-full max-w-[90%] sm:max-w-[400px]">
            Looks like this class is fully booked. You can only register with a
            "waitlist" status. If a spot opens up, you will be automatically
            added and a confirmation email will be sent to you.
          </div>
          <div className="flex flex-col justify-center w-full gap-3 mt-2 sm:flex-row sm:gap-6">
            <ButtonRegular
              className="font-semibold uppercase button-outline text-blue-primary"
              text="Cancel"
              onClick={handleSkip}
            />
            <ButtonRegular
              className="font-semibold uppercase button-primary"
              text="Join waitlist"
              onClick={handleJoinWaitlist}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistOverlay;
